import { createTheme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';

/**
 * Purchasing theme: calmer, more “template-like” (chic-cart-magic-main).
 * Scoped only to /purchasing routes via nested ThemeProvider.
 */
const purchasingTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      // Template primary ≈ hsl(233 64% 22%)
      main: '#1B1F57',
      contrastText: '#F8FAFC',
    },
    secondary: {
      // Keep secondary neutral (avoid “pink accent” noise)
      main: '#E2E8F0',
      contrastText: '#0F172A',
    },
    error: { main: '#DC2626' },
    warning: { main: '#F59E0B' },
    success: { main: '#16A34A' },
    info: { main: '#3B82F6' },
    background: {
      // Template background ≈ hsl(220 20% 98.5%)
      default: '#F6F8FB',
      paper: '#FFFFFF',
    },
    text: {
      // Template foreground ≈ hsl(222 47% 11%)
      primary: '#0F172A',
      secondary: '#475569',
    },
    divider: '#E2E8F0',
  },
  typography: {
    fontFamily:
      '"Inter", "Roboto", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    h1: { fontWeight: 650, letterSpacing: '-0.02em' },
    h2: { fontWeight: 650, letterSpacing: '-0.015em' },
    h3: { fontWeight: 650, letterSpacing: '-0.01em' },
    h4: { fontWeight: 650, letterSpacing: '-0.01em' },
    h5: { fontWeight: 650 },
    h6: { fontWeight: 650 },
    body1: { lineHeight: 1.55 },
    body2: { lineHeight: 1.45 },
    button: { textTransform: 'none', fontWeight: 600 },
  },
  shape: {
    borderRadius: 10,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#F6F8FB',
          backgroundImage:
            'linear-gradient(180deg, rgba(248, 250, 252, 1), rgba(241, 245, 249, 1))',
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 10,
          padding: '0.5rem 1rem',
          transition:
            'background-color 150ms ease, box-shadow 150ms ease, border-color 150ms ease',
        },
        contained: {
          backgroundImage: 'none',
          boxShadow: '0 1px 2px rgba(15, 23, 42, 0.06)',
          '&:hover': {
            backgroundImage: 'none',
            boxShadow: '0 4px 12px rgba(15, 23, 42, 0.10)',
          },
          '&:active': {
            boxShadow: '0 1px 2px rgba(15, 23, 42, 0.06)',
          },
        },
        outlined: {
          borderWidth: 1,
          '&:hover': {
            borderWidth: 1,
            backgroundColor: alpha('#1B1F57', 0.04),
          },
        },
        text: {
          '&:hover': {
            backgroundColor: alpha('#1B1F57', 0.04),
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        elevation1: {
          boxShadow: '0 1px 2px rgba(15, 23, 42, 0.06)',
          border: '1px solid #E2E8F0',
        },
        elevation2: {
          boxShadow: '0 4px 12px rgba(15, 23, 42, 0.08)',
          border: '1px solid #E2E8F0',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          position: 'relative',
          borderRadius: 12,
          boxShadow: '0 1px 2px rgba(15, 23, 42, 0.06)',
          border: '1px solid #E2E8F0',
          overflow: 'hidden',
          transition: 'box-shadow 150ms ease, border-color 150ms ease',
          '&:hover': {
            transform: 'none',
            boxShadow: '0 6px 18px rgba(15, 23, 42, 0.10)',
            borderColor: '#D7DFEA',
          },
          '&::after': {
            display: 'none',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(255, 255, 255, 0.85)',
          backdropFilter: 'blur(10px)',
          color: '#0F172A',
          boxShadow: '0 1px 2px rgba(15, 23, 42, 0.06)',
          borderBottom: '1px solid #E2E8F0',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 10,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#D6DEE8',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#B8C2D0',
              borderWidth: 1,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#1B1F57',
              borderWidth: 1,
              boxShadow: `0 0 0 3px ${alpha('#1B1F57', 0.12)}`,
            },
          },
          '& .MuiInputLabel-root': {
            '&.Mui-focused': {
              color: '#1B1F57',
            },
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid #E2E8F0',
          padding: '12px 14px',
        },
        head: {
          fontWeight: 650,
          backgroundColor: '#F1F5F9',
          color: '#0F172A',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: '#F8FAFC',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          fontWeight: 600,
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: '#E2E8F0',
        },
      },
    },
  },
});
export default purchasingTheme;
